import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { Result } from '@blade/std-lib'
import { useHapticFeedback } from '@blade/ui-kit'
import { basicModeAtom, setBasicModeAction } from '@root/entities/basic'
import { useTranslation } from '@root/shared/localization'
import { mediaMaxMobile } from '@root/shared/ui-kit'

import type { TradingMode } from '@root/entities/basic'
import type { TransKey } from '@root/shared/localization'

/**
 * Choosing how this account trades.
 *
 * The mode has driven the trading panel, the portfolio, the quick buy and the
 * extension warning since Basic was built, and until now there was no way to
 * change it here — only from the bot. Everything beneath this existed:
 * `GET`/`POST /api/basic/mode`, `basicModeAtom`, `setBasicModeAction`. What
 * was missing was the control.
 *
 * Two things the screen has to say, because a reader cannot find either out
 * by trying it:
 *
 * - **Switching moves no money.** Each side keeps its own balance — the
 *   wallets keep theirs and Basic keeps its own — and the choice only decides
 *   which one the screens spend from. Somebody who fears their funds will be
 *   swept somewhere will not touch the switch otherwise.
 * - **It is an account setting, not a device one.** The bot reads the same
 *   field, so changing it here changes it there.
 */

const MODES: ReadonlyArray<{
  mode: TradingMode
  title: TransKey
  description: TransKey
}> = [
  {
    mode: 'basic',
    title: 'settings.trading-mode.basic',
    description: 'settings.trading-mode.basic.description',
  },
  {
    mode: 'advanced',
    title: 'settings.trading-mode.advanced',
    description: 'settings.trading-mode.advanced.description',
  },
]

export const SettingsTradingMode = () => {
  const { t } = useTranslation()
  const haptics = useHapticFeedback()
  const [mode] = useAtom(basicModeAtom)
  const setMode = useAction(setBasicModeAction)
  /** Which one is being switched to, so the other cannot be pressed meanwhile. */
  const [switching, setSwitching] = useState<TradingMode | undefined>()

  const handleSelect = async (next: TradingMode) => {
    // The mode is not known until the account has answered, and pressing
    // before then would send a switch nobody asked for.
    if (mode === undefined || next === mode || switching) {
      return
    }
    haptics.impactOccurred('light')
    setSwitching(next)
    try {
      const result = await setMode(next)
      // A refusal leaves the old mode showing, which is the truth: the
      // account still trades the way it did.
      if (Result.isFailure(result)) {
        return
      }
    } finally {
      setSwitching(undefined)
    }
  }

  return (
    <Root>
      <Items>
        {MODES.map((item) => {
          const isActive = item.mode === mode
          return (
            <Item
              key={item.mode}
              type="button"
              $isActive={isActive}
              aria-pressed={isActive}
              disabled={mode === undefined || switching !== undefined}
              onClick={() => void handleSelect(item.mode)}
            >
              <ItemHead>
                <Title>{t(item.title)}</Title>
                {isActive ? (
                  <Current>{t('common.current')}</Current>
                ) : undefined}
              </ItemHead>
              <Description>{t(item.description)}</Description>
            </Item>
          )
        })}
      </Items>
      <Note>{t('settings.trading-mode.note')}</Note>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
`

const Items = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;

  ${mediaMaxMobile(css`
    grid-template-columns: 1fr;
  `)}
`

const Item = styled.button<{ $isActive: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 10px;
  cursor: pointer;
  background: var(--appearance-1);
  outline: 1px solid
    ${(x) => (x.$isActive ? 'var(--primary-9)' : 'transparent')};
  color: ${(x) => (x.$isActive ? 'var(--text-12)' : 'var(--text-10)')};

  &:hover:not(:disabled) {
    outline-color: ${(x) =>
      x.$isActive ? 'var(--primary-9)' : 'var(--primary-6)'};
    color: ${(x) => (x.$isActive ? 'var(--text-12)' : 'var(--text-11)')};
  }

  &:disabled {
    cursor: default;
  }
`

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
`

const Current = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: var(--primary-9);
`

const Description = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-10);
`

const Note = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: var(--text-9);
`
